"use client";
import { useQuery } from "@apollo/client/react";
import Image from "next/image";

import { useFocusMarketData } from "@/contexts/focus-market-data";
import { AccountStatusByPublicKeyDocument } from "@/graphql/codegen/graphql";
import { MIN_WHALE_USD_BALANCE, WHALE_ICON } from "@/utils/constants";
import { centsToUSD, formatUSD } from "@/utils/currency";
import type { PartialAccount } from "@/utils/types";

import { InfoTooltip } from "./info-tooltip";

type UserWhaleIconProps = {
  account?: PartialAccount;
  publicKey?: string;
  width?: number;
  height?: number;
  className?: string;
};

export const UserWhaleIcon = ({
  account,
  width = 32,
  height = 32,
  publicKey = account?.publicKey,
  className,
}: UserWhaleIconProps) => {
  if (!publicKey) {
    throw new Error(
      "publicKey is required, either by setting it directly or from an `account` object.",
    );
  }

  const accountBalanceUsdCents =
    account?.totalBalanceUsdCents ??
    account?.accountWealth?.totalBalanceUsdCents ??
    null;
  const hasAccountBalanceUsdCents = accountBalanceUsdCents != null;

  const { data, loading } = useQuery(AccountStatusByPublicKeyDocument, {
    variables: { publicKey },
    skip: hasAccountBalanceUsdCents,
  });
  const { loading: marketDataLoading } = useFocusMarketData();

  const balanceUsdCents =
    accountBalanceUsdCents ??
    data?.accountByPublicKey?.totalBalanceUsdCents ??
    data?.accountByPublicKey?.accountWealth?.totalBalanceUsdCents ??
    0;

  const balanceUsd = centsToUSD(balanceUsdCents);

  const showWhaleIcon =
    !loading && !marketDataLoading && balanceUsd > MIN_WHALE_USD_BALANCE;

  if (!showWhaleIcon) return null;

  return (
    <InfoTooltip
      text={
        <span className="text-xs text-muted">~{formatUSD(balanceUsd)}</span>
      }
    >
      <Image
        src={WHALE_ICON}
        unoptimized
        alt="whale"
        {...{ width, height }}
        className={className}
      />
    </InfoTooltip>
  );
};
